import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { getReportContent, ReportsContentRequest } from "src/network/graphql/analyticsService";
import { organisationFilter, OrganisationFilterType } from "src/network/graphql/organisationService";
import { initialOrgList, orgList } from "../organisation/organisationSlice";
import { convertToCSV } from "./reportsContentConfig";
import { EMPTY_STRING } from "src/components/constant/constant";

export type ReportsContent = {
  report_id: string;
  report_date: string;
  report_content_id: string;
  report_type: string;
  object_id: string;
  object_type: string;
  data: object;
};

export type ReportsContentState = {
  loading: boolean;
  boardViewLoading: { [stage: string]: boolean };
  orgListLoading: boolean;
  downloadReportLoading: boolean;
  error: null | string;
  boardViewReportsContent: {
    [stage: string]: {
      list: ReportsContent[];
      size: number;
      page: number;
      hasMore: boolean;
    };
  };
  reportsContent: {
    list: ReportsContent[];
    size: number;
  };
  orgLists: {
    list: orgList[];
    total: number;
  };
};

const initialState: ReportsContentState = {
  loading: false,
  boardViewLoading: {
    STAGE_1: false,
    STAGE_2: false,
    STAGE_3: false,
    STAGE_4: false,
    STAGE_5: false
  },
  orgListLoading: false,
  downloadReportLoading: false,
  error: null,
  boardViewReportsContent: {
    STAGE_1: { list: [], size: 0, page: 0, hasMore: true },
    STAGE_2: { list: [], size: 0, page: 0, hasMore: true },
    STAGE_3: { list: [], size: 0, page: 0, hasMore: true },
    STAGE_4: { list: [], size: 0, page: 0, hasMore: true },
    STAGE_5: { list: [], size: 0, page: 0, hasMore: true }
  },
  reportsContent: {
    list: [],
    size: 0
  },
  orgLists: {
    list: initialOrgList,
    total: 0
  }
};

const reportContentSlice = createSlice({
  name: "reportsContent",
  initialState,
  reducers: {
    fetchFilesContentStart: (state) => {
      state.loading = true;
    },
    fetchFilesContentSuccess: (state, action) => {
      state.loading = false;
      state.reportsContent = action.payload;
    },
    fetchFilesContentFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchOrgListStart: (state) => {
      state.orgListLoading = true;
    },
    fetchOrgListSuccess: (state, action) => {
      state.orgListLoading = false;
      state.orgLists.list = action.payload?.list ?? [];
      state.orgLists.total = action.payload?.total ?? 0;
    },
    fetchOrgListFail: (state, action) => {
      state.orgListLoading = false;
      state.orgLists.list = [];
      state.orgLists.total = 0;
      state.error = action.payload;
    },
    downloadReportStart: (state) => {
      state.downloadReportLoading = true;
    },
    downloadReportSuccess: (state) => {
      state.downloadReportLoading = false;
    },
    downloadReportFail: (state, action) => {
      state.downloadReportLoading = false;
      state.error = action.payload;
    },
    fetchBoardViewReportContentStart: (state, action) => {
      state.boardViewLoading[action.payload] = true;
    },
    fetchBoardViewReportContentSuccess: (state, action) => {
      state.boardViewLoading[action.payload.stage] = false;
      const { stage, data } = action.payload;

      const existingIds = new Set(state.boardViewReportsContent[stage].list.map((item) => item.report_content_id));
      const newItems = data.list.filter((item: any) => !existingIds.has(item.report_content_id));

      state.boardViewReportsContent[stage].list = [...state.boardViewReportsContent[stage].list, ...newItems];
      state.boardViewReportsContent[stage].size = data.size;
      state.boardViewReportsContent[stage].page += 1;
      state.boardViewReportsContent[stage].hasMore = data.list.length === 5;
    },
    fetchBoardViewReportContentFail: (state, action) => {
      state.boardViewLoading[action.payload.stage] = false;
      state.error = action.payload;
    },
    clearBoardViewReportContent: (state, action) => {
      const stage = action.payload;

      state.boardViewReportsContent[stage].list = [];
      state.boardViewReportsContent[stage].page = 0;
      state.boardViewReportsContent[stage].hasMore = true;
    }
  }
});

export const {
  fetchFilesContentStart,
  fetchFilesContentSuccess,
  fetchFilesContentFail,
  fetchOrgListStart,
  fetchOrgListSuccess,
  fetchOrgListFail,
  downloadReportStart,
  downloadReportSuccess,
  downloadReportFail,
  fetchBoardViewReportContentStart,
  fetchBoardViewReportContentSuccess,
  fetchBoardViewReportContentFail,
  clearBoardViewReportContent
} = reportContentSlice.actions;

export const fetchReportsContentAction = (data: ReportsContentRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchFilesContentStart());
    try {
      const response = await getReportContent(data);
      const structuredResponse = response.data.search_report_content.report_content_list.map((data: any) => ({
        object_id: data.object_id,
        object_type: data.object_type,
        report_date: data.report_date,
        report_type: data.report_type,
        data: JSON.parse(data.data)
      }));

      dispatch(
        fetchFilesContentSuccess({
          list: structuredResponse,
          size: response.data.search_report_content.total_size
        })
      );
    } catch (error) {
      dispatch(fetchFilesContentFail(error));
    }
  };
};

export const fetchOrgListAction = (data: OrganisationFilterType) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchOrgListStart());
    try {
      const response = await organisationFilter(data);

      const structuredResponse = response.data.search_organisation.organisation_listing.map((org: any) => ({
        organisationId: org.organisation_id,
        id: org.organisation_id,
        active: org.is_active,
        type: org.type,
        referralCode: org?.referral_code,
        name: org.name,
        createdAt: org.created_at,
        referenceName: org.contact.name,
        phone: org.contact.phone
      }));

      dispatch(
        fetchOrgListSuccess({
          list: structuredResponse,
          total: response.data.search_organisation.total_size
        })
      );
    } catch (error) {
      dispatch(fetchOrgListFail(error));
    }
  };
};

export const downloadReportAction = (data: ReportsContentRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(downloadReportStart());
    try {
      const response = await getReportContent(data);
      const structuredResponse = response.data.search_report_content.report_content_list.map((data: any) => ({
        object_id: data.object_id,
        object_type: data.object_type,
        report_date: data.report_date,
        report_type: data.report_type,
        data: JSON.parse(data.data)
      }));

      const csv = convertToCSV(structuredResponse);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.setAttribute("hidden", EMPTY_STRING);
      a.setAttribute("href", url);
      a.setAttribute("download", "organisation_report_content.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      dispatch(downloadReportSuccess());
    } catch (error) {
      console.error("Error downloading report content:", error);
      dispatch(downloadReportFail(error));
    }
  };
};

export const fetchBoardViewReportsContentAction = (data: ReportsContentRequest, clearData: boolean = false) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchBoardViewReportContentStart(data.filter.report_content_filter.stages));
    try {
      if (clearData) {
        dispatch(clearBoardViewReportContent(data.filter.report_content_filter.stages));
      }
      const response = await getReportContent(data);

      const structuredResponse = response.data.search_report_content.report_content_list.map((data: any) => ({
        object_id: data.object_id,
        report_content_id: data.report_content_id,
        object_type: data.object_type,
        report_date: data.report_date,
        report_type: data.report_type,
        data: JSON.parse(data.data)
      }));

      dispatch(
        fetchBoardViewReportContentSuccess({
          stage: data.filter.report_content_filter.stages,
          data: {
            list: structuredResponse,
            size: response.data.search_report_content.total_size
          }
        })
      );
    } catch (error) {
      dispatch(fetchBoardViewReportContentFail({ stage: data.filter.report_content_filter.stages, error }));
    }
  };
};

export default reportContentSlice.reducer;
